import React, { useState, useEffect } from 'react';
import GameCanvas from './components/GameCanvas';
import UIOverlay from './components/UIOverlay';
import SidePanel from './components/SidePanel';
import TutorialOverlay from './components/TutorialOverlay';
import StarField from './components/StarField';
import { audioManager } from './utils/audio';
import { STORAGE_KEYS, LEVEL_THRESHOLDS } from './constants';
import { RotateCcw, Award } from 'lucide-react';

export default function App() {
  const [score, setScore] = useState(() => {
    if (typeof window === 'undefined') return 0;
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.GAME_STATE);
      return saved ? (JSON.parse(saved).score || 0) : 0;
    } catch { return 0; }
  });

  const [highScore, setHighScore] = useState(() => {
    if (typeof window === 'undefined') return 0;
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.HIGH_SCORE);
      return saved ? parseInt(saved) : 0;
    } catch { return 0; }
  });

  const [isNewBest, setIsNewBest] = useState(false);
  const [nextOrbIndex, setNextOrbIndex] = useState(0); 
  const [futureOrbIndex, setFutureOrbIndex] = useState(0); 
  const [isMuted, setIsMuted] = useState(false);
  const [gameKey, setGameKey] = useState<number>(Date.now()); 
  const [isPaused, setIsPaused] = useState(false);
  const [showGameOver, setShowGameOver] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  const getLevelInfo = (currentScore: number) => {
      let level = 1;
      let nextThreshold = LEVEL_THRESHOLDS[1];
      let prevThreshold = LEVEL_THRESHOLDS[0];

      for (let i = 0; i < LEVEL_THRESHOLDS.length; i++) {
          if (currentScore >= LEVEL_THRESHOLDS[i]) {
              level = i + 1;
              prevThreshold = LEVEL_THRESHOLDS[i];
              nextThreshold = LEVEL_THRESHOLDS[i+1] || (LEVEL_THRESHOLDS[i] * 1.5); 
          }
      }
      
      const range = nextThreshold - prevThreshold;
      const progress = Math.min(1, Math.max(0, (currentScore - prevThreshold) / range));
      
      return { level, progress };
  };

  const { level, progress } = getLevelInfo(score);

  useEffect(() => {
    const tutorialSeen = localStorage.getItem(STORAGE_KEYS.TUTORIAL_SEEN);
    if (!tutorialSeen) {
      setShowTutorial(true);
      setIsPaused(true);
    }
  }, []);

  useEffect(() => {
    if (score > highScore) {
      setHighScore(score);
      setIsNewBest(true);
      localStorage.setItem(STORAGE_KEYS.HIGH_SCORE, score.toString());
    }
  }, [score, highScore]);

  const handleScoreUpdate = (points: number) => {
    setScore(prev => prev + points);
  };

  const handleQueueUpdate = (nextIndex: number, futureIndex: number) => {
    setNextOrbIndex(nextIndex);
    setFutureOrbIndex(futureIndex);
  };

  const handleToggleMute = () => {
    const muted = audioManager.toggleMute();
    setIsMuted(muted);
  };

  const handleReset = () => {
    wipeSaveAndReset();
  };

  const wipeSaveAndReset = () => {
    localStorage.removeItem(STORAGE_KEYS.GAME_STATE);
    setScore(0);
    setIsNewBest(false);
    setNextOrbIndex(0);
    setFutureOrbIndex(0);
    setIsPaused(false);
    setShowGameOver(false);
    setGameKey(Date.now());
  };

  const handleGameOver = () => {
    setIsPaused(true);
    setShowGameOver(true);
    localStorage.removeItem(STORAGE_KEYS.GAME_STATE);
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    setIsPaused(false);
    localStorage.setItem(STORAGE_KEYS.TUTORIAL_SEEN, 'true');
  };

  return (
    <div className="h-[100dvh] w-full bg-slate-950 text-slate-100 overflow-hidden relative selection:bg-none font-sans grid grid-cols-1 md:grid-cols-[1fr_auto_1fr]">
      
      {/* Background Layer */}
      <StarField />
      
      <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none z-50 mix-blend-overlay"></div>
      <div className="absolute inset-0 scanlines opacity-20 pointer-events-none z-50"></div>
      
      <div className="hidden md:block relative z-10 h-full border-r border-white/5 bg-slate-900/30 backdrop-blur-sm">
          <SidePanel side="left" />
      </div>

      <div className="relative z-20 flex flex-col items-center justify-center h-full w-full p-3">
          <div 
            className={`relative flex flex-col shadow-2xl rounded-xl overflow-hidden ring-1 ring-white/10 bg-slate-900/80 backdrop-blur-md transition-transform duration-[3s] ${!showGameOver && 'animate-pulse-slow'}`}
            style={{
                height: 'min(calc(100dvh - 2rem), 900px)',
                aspectRatio: '400/700',
                width: 'auto',
                maxWidth: '100%'
            }}
          >
            <div className="bg-slate-900/80 border-b border-white/5 p-2 shrink-0 z-30 relative">
                <UIOverlay 
                  score={score}
                  highScore={highScore}
                  nextOrbIndex={nextOrbIndex}
                  futureOrbIndex={futureOrbIndex}
                  level={level}
                  progress={progress}
                  isMuted={isMuted}
                  onToggleMute={handleToggleMute}
                  onReset={handleReset}
                />
            </div>

            <div className="flex-1 relative w-full min-h-0 bg-transparent">
                <GameCanvas 
                  key={gameKey} 
                  onScoreUpdate={handleScoreUpdate}
                  onQueueUpdate={handleQueueUpdate}
                  onGameOver={handleGameOver}
                  isPaused={isPaused}
                  initialScore={score}
                  level={level}
                />

                {showGameOver && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center p-6 bg-slate-950/90 backdrop-blur-xl animate-in zoom-in-95 fade-in duration-500">
                        <div className="w-full text-center relative">
                            {isNewBest && (
                                <div className="absolute -top-16 left-1/2 -translate-x-1/2 flex flex-col items-center animate-bounce">
                                    <Award size={48} className="text-yellow-400 drop-shadow-[0_0_15px_rgba(250,204,21,0.5)]" />
                                    <span className="text-yellow-400 text-xs font-black uppercase tracking-widest">Personal Best</span>
                                </div>
                            )}
                            
                            <h2 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-indigo-300 to-cyan-300 mb-2 drop-shadow-[0_0_15px_rgba(99,102,241,0.5)]">
                                CRITICAL MASS
                            </h2>
                            <p className="text-slate-400 mb-8 font-medium tracking-widest text-xs uppercase">Simulation Terminated</p>
                            
                            <div className="grid grid-cols-2 gap-3 mb-8">
                                <div className="bg-slate-900/50 p-4 rounded-xl border border-white/10 backdrop-blur-md">
                                    <div className="text-[10px] text-slate-500 uppercase font-bold">Final Score</div>
                                    <div className="text-2xl text-white font-mono">{score.toLocaleString()}</div>
                                </div>
                                <div className={`p-4 rounded-xl border backdrop-blur-md transition-colors ${isNewBest ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-slate-900/50 border-white/10'}`}>
                                    <div className="text-[10px] text-slate-500 uppercase font-bold">Best Mass</div>
                                    <div className={`text-2xl font-mono ${isNewBest ? 'text-yellow-400 animate-pulse' : 'text-slate-300'}`}>{highScore.toLocaleString()}</div>
                                </div>
                            </div>

                            <button 
                                onClick={wipeSaveAndReset}
                                className="w-full py-4 bg-white text-slate-950 font-black tracking-wider uppercase rounded-xl hover:bg-indigo-50 active:scale-95 transition-all flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(255,255,255,0.2)]"
                            >
                                <RotateCcw size={18} className="stroke-[3px]" />
                                Reboot System
                            </button>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="absolute bottom-1 left-0 w-full text-center pointer-events-none opacity-40 z-10">
                <p className="text-[10px] font-mono tracking-widest uppercase text-slate-400">
                    <span className="hidden sm:inline">Drag to Aim • </span>Release to Drop
                </p>
            </div>

            {showTutorial && <TutorialOverlay onComplete={handleTutorialComplete} />}
          </div>
      </div>

      <div className="hidden md:block relative z-10 h-full border-l border-white/5 bg-slate-900/30 backdrop-blur-sm">
          <SidePanel side="right" />
      </div>

      <style>{`
        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.005); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 6s ease-in-out infinite;
        }
      `}</style>

    </div>
  );
}